import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Row, Col, Button } from "react-bootstrap";
import dataServices from "../../apiServices/data.services";
import razorpayService from "../../apiServices/razorpay.service";
import { useAlert } from "react-alert";
import { useNavigate } from "react-router-dom";
import { Can } from "../../context/AuthProvider";
import { handleValidationError } from "../../components/CommonFunctions";

const AddPayment = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("manual"); // 'manual' or 'online'
  const alert = useAlert();
  const navigate = useNavigate();

  useEffect(() => {
    dataServices
      .getSubscriptions()
      .then((res) => {
        if (res.status === 200) {
          setPlans(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const initialValues = {
    subscription_id: "",
    amount: "",
    payment_date: "",
    status: "completed",
    transaction_id: "",
    description: "",
    discount_amount: "",
  };

  const validationSchema = Yup.object().shape({
    subscription_id: Yup.string().required("Subscription is required"),
    amount: Yup.number().typeError("Amount must be numeric").required("Amount is required"),
    payment_date: Yup.string().required("Payment date is required"),
  });

  const handleSubmit = (values) => {
    if (paymentMethod === "online") {
      handleOnlinePayment(values);
    } else {
      handleManualPayment(values);
    }
  };

  const handleManualPayment = (values) => {
    setLoading(true);
    dataServices
      .addPayment({ ...values, payment_type: "manual" })
      .then((res) => {
        if (res.status === 201 || res.status === 200) {
          alert.success("Payment recorded successfully");
          navigate("/payments");
        }
      })
      .catch((err) => {
        alert.error(handleValidationError(err));
      })
      .finally(() => setLoading(false));
  };

  const handleOnlinePayment = async (values) => {
    if (!values.subscription_id || !values.amount) {
      alert.error("Please select subscription and enter amount");
      return;
    }

    setLoading(true);
    try {
      // Step 1: Create order on backend
      const orderRes = await dataServices.createRazorpayOrder({
        subscription_id: values.subscription_id,
        amount: values.amount,
        description: values.description || "Subscription Payment",
      });

      if (orderRes.status !== 200 && orderRes.status !== 201) {
        throw new Error("Failed to create payment order");
      }

      const subscription = plans.find((p) => p.id === parseInt(values.subscription_id));

      // Step 2: Initiate Razorpay payment
      await razorpayService.initiatePayment({
        orderId: orderRes.data.order_id,
        amount: Math.round(values.amount * 100), // Convert to paise
        currency: "INR",
        customerName: orderRes.data.customer_name || "Customer",
        customerEmail: orderRes.data.customer_email || "",
        customerPhone: orderRes.data.customer_phone || "",
        description: values.description || "Subscription Payment",
        onSuccess: (response) => handlePaymentSuccess(response, values, orderRes.data),
        onError: (error) => {
          alert.error("Payment failed: " + error);
          setLoading(false);
        },
      });
    } catch (error) {
      alert.error(handleValidationError(error));
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (razorpayResponse, formValues, orderData) => {
    try {
      // Step 3: Verify payment on backend
      const verifyRes = await dataServices.verifyRazorpayPayment({
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_order_id: razorpayResponse.razorpay_order_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
        subscription_id: formValues.subscription_id,
        amount: formValues.amount,
        payment_date: formValues.payment_date,
        description: formValues.description,
        discount_amount: formValues.discount_amount || 0,
        payment_type: "online",
      });

      if (verifyRes.status === 200 || verifyRes.status === 201) {
        alert.success("Payment successful!");
        navigate("/payments");
      }
    } catch (error) {
      alert.error("Payment verification failed: " + handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-2 pb-1 fw-bold text-black">Add Payment</h6>
      </div>
      <Can I="add" this="Payment">
        {/* Payment Method Selection */}
        <div className="mb-4 p-3 bg-light rounded">
          <label className="fw-bold mb-3 d-block">Payment Method <span className="text-danger">*</span></label>
          <div className="form-check">
            <input
              type="radio"
              className="form-check-input"
              id="manualPayment"
              name="paymentMethod"
              value="manual"
              checked={paymentMethod === "manual"}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <label className="form-check-label" htmlFor="manualPayment">
              Manual Entry - Record payment manually
            </label>
          </div>
          <div className="form-check mt-2">
            <input
              type="radio"
              className="form-check-input"
              id="onlinePayment"
              name="paymentMethod"
              value="online"
              checked={paymentMethod === "online"}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <label className="form-check-label" htmlFor="onlinePayment">
              Online Payment (Razorpay) - Process payment securely
            </label>
          </div>
        </div>

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ errors, touched, values }) => (
            <Form>
              <Row>
                <Col sm={6}>
                  <div className="form-group">
                    <label htmlFor="subscription_id">Subscription <span className="text-danger">*</span></label>
                    <Field as="select" name="subscription_id" className={`form-control ${touched.subscription_id && errors.subscription_id ? "is-invalid" : ""}`}>
                      <option value="">Select subscription</option>
                      {plans.map((s) => (
                        <option key={s.id} value={s.id}>
                          #{s.id} - {s.plan?.name || "Plan"}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="subscription_id" component="div" className="invalid-feedback" />
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="form-group">
                    <label htmlFor="amount">Amount <span className="text-danger">*</span></label>
                    <Field name="amount" type="text" className={`form-control ${touched.amount && errors.amount ? "is-invalid" : ""}`} />
                    <ErrorMessage name="amount" component="div" className="invalid-feedback" />
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="form-group">
                    <label htmlFor="payment_date">Payment Date <span className="text-danger">*</span></label>
                    <Field name="payment_date" type="date" className={`form-control ${touched.payment_date && errors.payment_date ? "is-invalid" : ""}`} />
                    <ErrorMessage name="payment_date" component="div" className="invalid-feedback" />
                  </div>
                </Col>
                {paymentMethod === "manual" && (
                  <>
                    <Col sm={6}>
                      <div className="form-group">
                        <label htmlFor="status">Status</label>
                        <Field as="select" name="status" className="form-control">
                          <option value="completed">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                        </Field>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="form-group">
                        <label htmlFor="transaction_id">Transaction ID</label>
                        <Field name="transaction_id" type="text" className="form-control" />
                      </div>
                    </Col>
                  </>
                )}
                <Col sm={6}>
                  <div className="form-group">
                    <label htmlFor="discount_amount">Discount Amount</label>
                    <Field name="discount_amount" type="text" className="form-control" />
                  </div>
                </Col>
                <Col sm={12}>
                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <Field name="description" as="textarea" rows={3} className="form-control" placeholder="Optional" />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col className="footer mt-auto">
                  <Button
                    type="submit"
                    disabled={loading}
                    className={`btn w-100 ${paymentMethod === "online" ? "btn-success" : "btn-primary"}`}
                  >
                    {loading ? "Processing..." : paymentMethod === "online" ? "Process Payment" : "Save Payment"}
                  </Button>
                </Col>
              </Row>
              {paymentMethod === "online" && (
                <Row className="mt-3">
                  <Col>
                    <small className="text-info">
                      <strong>Note:</strong> Click "Process Payment" to securely process payment via Razorpay. You will be redirected to the payment gateway.
                    </small>
                  </Col>
                </Row>
              )}
            </Form>
          )}
        </Formik>
      </Can>
    </div>
  );
};

export default AddPayment;
